import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import type { BlocksContent } from '@strapi/blocks-react-renderer';
import { WIDGET_SPACING_CLASS } from '../../spacing';
import type { TwoColumnLayoutResolved } from './types';
import { SlotRenderer } from '@/components/SlotRenderer';
import { RichText } from '@/lib/render/RichText';
import {
  getSectionColorClass,
  type SectionBackgroundColor,
} from '@/lib/section-colors';

const RATIOS: Record<NonNullable<TwoColumnLayoutResolved['ratio']>, string> = {
  '1-1': 'lg:grid-cols-2',
  '2-1': 'lg:grid-cols-[2fr_1fr]',
  '1-2': 'lg:grid-cols-[1fr_2fr]',
};

function columnBgClass(value?: string): string | undefined {
  if (!value || value === 'none') {
    return undefined;
  }
  return getSectionColorClass(value as SectionBackgroundColor);
}

export function TwoColumnLayoutComponent({
  resolved,
}: {
  resolved: TwoColumnLayoutResolved;
}) {
  const {
    ratio = '1-1',
    leftBackground,
    rightBackground,
    leftColumn = [],
    rightColumn = [],
    leftFooter,
    rightFooter,
    spacing,
  } = resolved;
  const leftBg = columnBgClass(leftBackground);
  const rightBg = columnBgClass(rightBackground);
  const leftFooterNodes = leftFooter as BlocksContent | undefined;
  const rightFooterNodes = rightFooter as BlocksContent | undefined;

  return (
    <div
      className={classNames(
        'two-col-layout mx-auto max-w-6xl px-4 lg:px-9',
        WIDGET_SPACING_CLASS[spacing],
      )}
    >
      <div className={classNames('grid items-stretch gap-6', RATIOS[ratio])}>
        <div
          className={classNames(
            'relative h-full min-w-0',
            leftBg && `${leftBg} rounded-2xl p-6`,
          )}
        >
          <SlotRenderer blocks={leftColumn} zoneName="leftColumn" />
        </div>
        <div
          className={classNames(
            'relative h-full min-w-0',
            rightBg && `${rightBg} rounded-2xl p-6`,
          )}
        >
          <SlotRenderer blocks={rightColumn} zoneName="rightColumn" />
        </div>
      </div>
      {(leftFooterNodes?.length || rightFooterNodes?.length) && (
        <div className={classNames('mt-3 grid gap-6', RATIOS[ratio])}>
          <div className="text-foreground/60 min-w-0 text-xs [&_a]:underline">
            {leftFooterNodes && leftFooterNodes.length > 0 && (
              <RichText nodes={leftFooterNodes} />
            )}
          </div>
          <div className="text-foreground/60 min-w-0 text-xs [&_a]:underline">
            {rightFooterNodes && rightFooterNodes.length > 0 && (
              <RichText nodes={rightFooterNodes} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
